import React, { useState, useEffect, useCallback } from "react";
import {
  Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, TextField, Button, MenuItem, Select,
  FormControl, InputLabel, Chip, CircularProgress, Alert, Box,
  IconButton, Tooltip, TablePagination,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import api from "../../api/api";

const CleaningSessionManager = ({ year, month }) => {
  const [rows, setRows] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 5;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bookingsRes, staffRes] = await Promise.all([
        api.get(`/invoicing/cleaning-sessions/ended-bookings/?year=${year}&month=${month}`),
        api.get("/staff/"),
      ]);
      setRows(bookingsRes.data.map((r) => ({
        ...r,
        // local editable state
        _staffId: r.cleaning_session?.staff ?? "",
        _cleanStart: r.cleaning_session?.cleaning_start
          ? toLocalDatetimeString(r.cleaning_session.cleaning_start)
          : "",
        _cleanEnd: r.cleaning_session?.cleaning_end
          ? toLocalDatetimeString(r.cleaning_session.cleaning_end)
          : "",
        _notes: r.cleaning_session?.notes ?? "",
        _sessionId: r.cleaning_session?.id ?? null,
      })));
      setStaffList(staffRes.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data ? JSON.stringify(e.response.data) : e.message;
      console.error("CleaningSessionManager fetch error:", e);
      setError(`Failed to load data. [HTTP ${status ?? "network error"}] ${detail}`);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const saveSession = async (row, idx) => {
    setSaving((s) => ({ ...s, [idx]: true }));
    const payload = {
      booking: row.booking_id,
      staff: row._staffId || null,
      cleaning_start: row._cleanStart ? toISOString(row._cleanStart) : null,
      cleaning_end: row._cleanEnd ? toISOString(row._cleanEnd) : null,
      notes: row._notes,
    };
    try {
      if (row._sessionId) {
        await api.patch(`/invoicing/cleaning-sessions/${row._sessionId}/`, payload);
      } else {
        await api.post("/invoicing/cleaning-sessions/", payload);
      }
      fetchData();
    } catch (e) {
      setError("Save failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setSaving((s) => ({ ...s, [idx]: false }));
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {rows.length} ended bookings for {month}/{year}.
        Assign a cleaner and set the cleaning times, then save each row.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#2c5f8a" }}>
              {["Booking", "Apartment", "Booking End", "Staff", "Clean Start", "Clean End", "Hours", "Gross (€)", "Status", "Save"].map((h) => (
                <TableCell key={h} sx={{ color: "white", fontWeight: "bold", py: 1 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">No ended bookings for this period.</TableCell>
              </TableRow>
            ) : rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((row, idx) => {
              const hours = calcHours(row._cleanStart, row._cleanEnd);
              const staffMember = staffList.find((s) => s.id === row._staffId);
              const rate = getEffectiveHourlyRate(staffMember, row.end_date);
              const gross = hours && rate ? (hours * parseFloat(rate)).toFixed(2) : "–";
              return (
                <TableRow key={row.booking_id} sx={{ "&:nth-of-type(even)": { backgroundColor: "#f0f4f8" } }}>
                  <TableCell>#{row.booking_id}</TableCell>
                  <TableCell>{row.apartment}</TableCell>
                  <TableCell>{row.end_date}</TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={row._staffId}
                        onChange={(e) => updateRow(idx, "_staffId", e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value=""><em>— unassigned —</em></MenuItem>
                        {staffList.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.user_details?.first_name
                              ? `${s.user_details.first_name} ${s.user_details.last_name}`
                              : s.user_details?.username ?? `Staff #${s.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="datetime-local"
                      size="small"
                      value={row._cleanStart}
                      onChange={(e) => updateRow(idx, "_cleanStart", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 185 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="datetime-local"
                      size="small"
                      value={row._cleanEnd}
                      onChange={(e) => updateRow(idx, "_cleanEnd", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 185 }}
                    />
                  </TableCell>
                  <TableCell align="right">{hours !== null ? hours.toFixed(2) : "–"}</TableCell>
                  <TableCell align="right">{gross}</TableCell>
                  <TableCell>
                    {row._sessionId
                      ? <Chip label="Saved" color="success" size="small" />
                      : <Chip label="New" color="warning" size="small" />}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Save row">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={!!saving[idx]}
                          onClick={() => saveSession(row, idx)}
                        >
                          {saving[idx] ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[5]}
      />
    </Box>
  );
};

// ── helpers ──────────────────────────────────────────────────────────────────

/**
 * Return the hourly rate valid for a staff member on a given reference date.
 * Mirrors SalaryRecord.get_current_for_staff() on the backend.
 * Falls back to staff.hourly_rate when no salary record covers the date.
 */
function getEffectiveHourlyRate(staff, refDateStr) {
  if (!staff) return 0;
  const refDate = refDateStr ? new Date(refDateStr) : new Date();
  const records = staff.salary_records ?? [];
  // Records are already ordered by -start_date from the API
  const match = records.find((r) => {
    const start = new Date(r.start_date);
    const end = r.end_date ? new Date(r.end_date) : null;
    return (
      r.salary_type === "hourly" &&
      start <= refDate &&
      (end === null || end >= refDate)
    );
  });
  if (match) return match.amount;
  // Fallback to Staff.hourly_rate
  return staff.hourly_rate ? parseFloat(staff.hourly_rate) : 0;
}

function toLocalDatetimeString(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toISOString(localDatetimeStr) {
  return new Date(localDatetimeStr).toISOString();
}

function calcHours(start, end) {
  if (!start || !end) return null;
  const diff = new Date(end) - new Date(start);
  if (diff <= 0) return null;
  return diff / 3_600_000;
}

export default CleaningSessionManager;
